/**
 * useTourState — Cluster J.B (Feb 2026).
 *
 * Encapsulates the persisted-completion state for the onboarding tour.
 * Read/write goes through the canonical `lib/scopedStorage` so the
 * key shape stays `bayenah.scope.{org_id}.{user_id}.tour_completed_v1`.
 *
 * API:
 *   shouldRun        true iff: user authed, org_id resolved, scoped
 *                    "completed" flag is missing, AND `forceRun` is
 *                    not explicitly false. Recomputed reactively.
 *   markComplete()   write the scoped flag → shouldRun flips false on
 *                    the next render.
 *   replay()         clear the scoped flag + set the in-memory
 *                    `forceRun` to true so the host re-runs the tour
 *                    even if the user never refreshes.
 *
 * Anonymous / unresolved-org case: shouldRun is always false. The
 * tour entry effect must not fire until both `user` and the org_id
 * are available — otherwise we'd write to `noorg.nouser.*` and stash
 * a "completed" flag the real session never sees.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import {
  readScopedFlag,
  writeScopedFlag,
} from '@/lib/scopedStorage';

const TOUR_KEY = 'tour_completed_v1';
const REPLAY_EVENT = 'bayenah:tour-replay';

export function useTourState() {
  const { user } = useAuth();
  const { org } = useOrganization?.() || { org: null };

  const userId = user?.id || user?.user_id || null;
  // `useOrganization` returns `org` (or `null` for solo users without
  // an org assignment). Solo users still get the tour — `scopedKey`
  // falls back to "noorg" so their completion flag lives under
  // `bayenah.scope.noorg.{userId}.tour_completed_v1`.
  const orgId  = org?.org_id || org?.id || null;
  const authReady = Boolean(userId);

  // `forceRun` allows the Settings "Take the tour again" link to
  // re-fire the tour without a page refresh. It is intentionally NOT
  // persisted — only valid for the current React tree lifetime.
  //
  // Cross-instance sync: both `TopBar` and `AgentPreviewPage` mount
  // their own `useTourState()` instances. When one calls `replay()`,
  // the other has no React reason to re-render. We bridge via a
  // window event so all live instances re-read storage + force-run.
  const [forceRun, setForceRun] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onReplay = () => {
      setForceRun(true);
      setRefreshTick((n) => n + 1);
    };
    window.addEventListener(REPLAY_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_EVENT, onReplay);
  }, []);

  const completed = useMemo(
    () => (authReady ? readScopedFlag(orgId, userId, TOUR_KEY) : false),
    [authReady, orgId, userId, forceRun, refreshTick],
  );

  const shouldRun = authReady && (forceRun || !completed);

  const markComplete = useCallback(() => {
    if (!authReady) return;
    writeScopedFlag(orgId, userId, TOUR_KEY, true);
    setForceRun(false);
    setRefreshTick((n) => n + 1);
  }, [authReady, orgId, userId]);

  const replay = useCallback(() => {
    if (!authReady) return;
    writeScopedFlag(orgId, userId, TOUR_KEY, false);
    setForceRun(true);
    setRefreshTick((n) => n + 1);
    // Notify sibling instances (host page + topbar both run their own
    // `useTourState`). Without this dispatch the AgentPreviewPage's
    // memoised `completed` stays stale until the next external re-render.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(REPLAY_EVENT));
    }
  }, [authReady, orgId, userId]);

  return { shouldRun, markComplete, replay, authReady };
}
